import { Sparkles, FileText, Droplets, Leaf, AlertTriangle, ListChecks } from 'lucide-react';
import type { ProductSection, SectionType } from '../../types/products';
import { SECTION_TYPE_ORDER } from '../../types/products';

interface ProductSectionsProps {
  sections: ProductSection[];
}

const sectionIcons: Record<SectionType, typeof Sparkles> = {
  bullets: ListChecks,
  long_description: FileText,
  usage: Droplets,
  unique_ingredients: Leaf,
  ingredients_list: FileText,
  warnings: AlertTriangle,
};

const sectionColors: Record<SectionType, { bg: string; icon: string; border: string }> = {
  bullets: { bg: 'bg-violet-50 dark:bg-violet-900/30', icon: 'text-violet-600 dark:text-violet-400', border: 'border-violet-200 dark:border-violet-800' },
  long_description: { bg: 'bg-blue-50 dark:bg-blue-900/30', icon: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
  usage: { bg: 'bg-cyan-50 dark:bg-cyan-900/30', icon: 'text-cyan-600 dark:text-cyan-400', border: 'border-cyan-200 dark:border-cyan-800' },
  unique_ingredients: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
  ingredients_list: { bg: 'bg-amber-50 dark:bg-amber-900/30', icon: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
  warnings: { bg: 'bg-red-50 dark:bg-red-900/30', icon: 'text-red-600 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
};

export function ProductSections({ sections }: ProductSectionsProps) {
  // Sort sections by predefined order
  const sortedSections = [...sections].sort((a, b) => {
    const orderA = SECTION_TYPE_ORDER.indexOf(a.section_type);
    const orderB = SECTION_TYPE_ORDER.indexOf(b.section_type);
    return orderA - orderB;
  });

  if (sections.length === 0) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
      <div className="p-6 space-y-8">
        {sortedSections.map((section, index) => {
          const Icon = sectionIcons[section.section_type] || FileText;
          const colors = sectionColors[section.section_type] || { bg: 'bg-gray-50', icon: 'text-gray-600', border: 'border-gray-200' };
          const isWarning = section.section_type === 'warnings';

          return (
            <div key={section.id}>
              {/* Section Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-9 h-9 ${colors.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-4.5 h-4.5 ${colors.icon}`} />
                </div>
                <h3 className="text-lg font-semibold text-[#1A1A1A] dark:text-white">{section.title}</h3>
              </div>

              {/* Section Content */}
              <div className={`${isWarning ? `${colors.bg} ${colors.border} border rounded-lg p-4` : ''}`}>
                <div
                  className={`prose prose-sm max-w-none ${isWarning ? 'text-red-800 dark:text-red-300' : 'text-[#4A4A4A] dark:text-slate-300'} leading-relaxed`}
                  dangerouslySetInnerHTML={{ __html: formatContent(section.content) }}
                />
              </div>

              {/* Divider (except for last item) */}
              {index < sortedSections.length - 1 && (
                <div className="mt-8 border-b border-gray-100 dark:border-slate-700" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatContent(content: string): string {
  // Convert newlines and basic markdown to HTML
  let html = content
    // Escape any existing HTML first
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Convert markdown-style bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Convert markdown-style italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Handle paragraphs (double newlines)
    .replace(/\n\n/g, '</p><p>')
    // Handle line breaks
    .replace(/\n/g, '<br>');

  // Handle bullet points
  const lines = html.split('<br>');
  let inList = false;
  const processedLines = lines.map((line) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
      const listItem = `<li>${trimmed.substring(2)}</li>`;
      if (!inList) {
        inList = true;
        return `<ul class="list-disc list-inside space-y-1 my-2">${listItem}`;
      }
      return listItem;
    } else if (inList) {
      inList = false;
      return `</ul>${line}`;
    }
    return line;
  });

  html = processedLines.join('');
  if (inList) {
    html += '</ul>';
  }

  // Wrap in paragraph if not already
  if (!html.startsWith('<p>') && !html.startsWith('<ul')) {
    html = `<p>${html}</p>`;
  }

  return html;
}
